/**
 * FAULT CHANNEL — one place errors go instead of vanishing.
 *
 * The game swallows a lot on purpose: a failed audio decode or a missing poster
 * should never take the screen down. The problem is the swallow points that are
 * NOT cosmetic. The reducer answers an edit it cannot use with a bare
 * `return state`, so the action just... does not happen. That is how a Publish
 * button stayed dead for every reel whose caption had been edited: no throw, no
 * log, no screen, nothing to search for.
 *
 * So: cosmetic failures still swallow, but anything that means "the thing you
 * asked for did not happen" calls `reportFault`. It always reaches the console,
 * it is kept in a small ring buffer readable from the console via
 * `window.__deskFaults()`, and subscribers (the fatal screen) can react.
 *
 * This is deliberately NOT an analytics hook: it makes no network calls and
 * holds no PII, so it works offline and in tests exactly as it does in prod.
 */

export interface Fault {
  /** Where it happened, e.g. "reducer/PUBLISH". */
  where: string;
  message: string;
  /** True when the app cannot sensibly continue (render crash). */
  fatal: boolean;
  stack?: string;
  at: number;
}

const MAX = 25;
const faults: Fault[] = [];
const listeners = new Set<(f: Fault) => void>();

function messageOf(err: unknown): string {
  if (err instanceof Error) return err.message;
  if (typeof err === "string") return err;
  try {
    return JSON.stringify(err);
  } catch {
    return String(err);
  }
}

export function reportFault(where: string, err: unknown, opts: { fatal?: boolean } = {}): Fault {
  const fault: Fault = {
    where,
    message: messageOf(err),
    fatal: opts.fatal ?? false,
    stack: err instanceof Error ? err.stack : undefined,
    at: Date.now(),
  };
  faults.push(fault);
  if (faults.length > MAX) faults.shift();
  // Always loud. A swallowed error that reaches no one is the bug we are fixing.
  console.error(`[desk] ${where}: ${fault.message}`, err);
  for (const fn of listeners) {
    try {
      fn(fault);
    } catch {
      // A listener must never be able to escalate a fault into a second fault.
    }
  }
  return fault;
}

export function subscribeFaults(fn: (f: Fault) => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

export function recentFaults(): readonly Fault[] {
  return [...faults];
}

/** Console handle for debugging a live session: `__deskFaults()`. */
export function installFaultConsole(): void {
  if (typeof window === "undefined") return;
  (window as unknown as { __deskFaults?: () => readonly Fault[] }).__deskFaults = recentFaults;
}
